import React from 'react'
import { Box, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CustomTooltip from '../../../../components/CustomTooltip'
import { grey } from '../../../../colors'

/** Bolded inline highlight for the key terms inside a tooltip bullet. */
const Em: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 700 }}>{children}</Box>
)

/**
 * Shared shell for the Survey Score explanation tooltip. Renders the title plus a
 * bulleted list; copy varies by surface (general reports vs. instructor dashboard)
 * but the title, bullets, and styling stay in sync.
 */
const SurveyScoreTooltipShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ fontSize: 12, fontWeight: 600, color: 'white', mb: 0.5 }}
    >
      Survey Score
    </Typography>
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.5,
        color: 'white',
        fontSize: 12,
        '& li': { mb: 0.5 },
        '& li:last-of-type': { mb: 0 }
      }}
    >
      {children}
    </Box>
  </Box>
)

/** Default copy used on division/section reports. */
export const SurveyScoreTooltipContent = (): JSX.Element => (
  <SurveyScoreTooltipShell>
    <li>Average score across all <Em>Linear Scale</Em> questions in this survey.</li>
    <li>Only <Em>Linear Scale</Em> questions count — other question types <Em>don&apos;t</Em>.</li>
    <li><Em>&quot;N/A&quot;</Em> answers are left out.</li>
  </SurveyScoreTooltipShell>
)

/** Instructor-dashboard copy — score is scoped to one instructor + section, cross-lists excluded. */
export const InstructorSurveyScoreTooltipContent = (): JSX.Element => (
  <SurveyScoreTooltipShell>
    <li>Average rating across the <Em>Linear Scale</Em> questions, on the same scale those questions use (e.g. <Em>1–5</Em>).</li>
    <li>Calculated specifically for <Em>this instructor and this course section</Em>.</li>
    <li><Em>Cross-listed sections</Em> tied to this section are <Em>excluded</Em>.</li>
    <li>Other question types and <Em>&quot;N/A&quot;</Em> answers don&apos;t count toward it.</li>
  </SurveyScoreTooltipShell>
)

/**
 * Builds a column-header renderer that shows the header label alongside an info
 * icon carrying the given tooltip content. Pass to CustomTable's `headerOverrides`
 * keyed by the survey score field.
 */
const buildSurveyScoreHeader = (content: JSX.Element) =>
  function SurveyScoreHeader (headerName: string): JSX.Element {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <span>{headerName}</span>
        <CustomTooltip title={content} placement="top">
          <InfoOutlinedIcon
            aria-label="About the survey score"
            sx={{ color: grey[600], fontSize: 16, cursor: 'pointer', display: 'block' }}
          />
        </CustomTooltip>
      </Box>
    )
  }

export const renderSurveyScoreHeader = buildSurveyScoreHeader(<SurveyScoreTooltipContent />)
export const renderInstructorSurveyScoreHeader = buildSurveyScoreHeader(<InstructorSurveyScoreTooltipContent />)
